// src/pages/reports/PreferenceMatchReport.tsx
//
// Phase 9 (v1.5.0): Preference-vs-Allocation analytics.
//
// Reads the current open period's HR-approved intentions + the active grid,
// computes per-teacher match metrics (FTE, day-off, #1 area, #1 subject, RUAH)
// and renders summary cards + a drill-down table with CSV export.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, RefreshCw, AlertCircle, TrendingUp, Calendar, Target, Sparkles } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import { useStabilizedGrid } from '../../contexts/StabilizedContexts';
import { intentionsService } from '../../services/intentionsService';
import { TeachingIntention } from '../../types/intentions';
import {
  computePreferenceAnalytics,
  analyticsRowsToCsv,
  PreferenceAnalyticsResult,
  TeacherMatchRow
} from '../../services/preferenceAnalyticsService';
import { displayLearningArea } from '../../constants/learningAreas';

const pct = (x: number): string => `${Math.round(x * 100)}%`;

function statusDot(kind: 'good' | 'warn' | 'bad' | 'neutral'): string {
  switch (kind) {
    case 'good': return 'bg-ok-solid';
    case 'warn': return 'bg-warn-solid';
    case 'bad': return 'bg-danger-solid';
    default:    return 'bg-ink-400';
  }
}

function fteBadge(m: TeacherMatchRow['fteMatch']) {
  switch (m) {
    case 'match':   return { cls: 'bg-ok-bg text-ok-fg border-ok-border', label: 'Match' };
    case 'over':    return { cls: 'bg-warn-bg text-warn-fg border-warn-border', label: 'Over' };
    case 'under':   return { cls: 'bg-warn-bg text-warn-fg border-warn-border', label: 'Under' };
    case 'discuss': return { cls: 'bg-navy-100 text-navy-800 border-navy-200', label: 'Discuss' };
    default:        return { cls: 'bg-ink-100 text-ink-700 border-ink-200', label: '—' };
  }
}

function dayBadge(m: TeacherMatchRow['dayMatch']) {
  switch (m) {
    case 'match':    return { cls: 'bg-ok-bg text-ok-fg border-ok-border', label: 'Honoured' };
    case 'conflict': return { cls: 'bg-danger-bg text-danger-fg border-danger-border', label: 'Conflict' };
    case 'discuss':  return { cls: 'bg-navy-100 text-navy-800 border-navy-200', label: 'Discuss' };
    default:         return { cls: 'bg-ink-100 text-ink-700 border-ink-200', label: '—' };
  }
}

function metBadge(m: TeacherMatchRow['topAreaMet'] | TeacherMatchRow['topSubjectMet']) {
  switch (m) {
    case 'met':             return { cls: 'bg-ok-bg text-ok-fg border-ok-border', label: '✓ Met' };
    case 'not-met':         return { cls: 'bg-danger-bg text-danger-fg border-danger-border', label: '✗ Not met' };
    default:                return { cls: 'bg-ink-100 text-ink-500 border-ink-200', label: '—' };
  }
}

function ruahBadge(m: TeacherMatchRow['ruahSatisfaction']) {
  switch (m) {
    case 'full':          return { cls: 'bg-ok-bg text-ok-fg border-ok-border', label: 'Full' };
    case 'partial':       return { cls: 'bg-warn-bg text-warn-fg border-warn-border', label: 'Partial' };
    case 'none':          return { cls: 'bg-danger-bg text-danger-fg border-danger-border', label: 'None' };
    default:              return { cls: 'bg-ink-100 text-ink-500 border-ink-200', label: '—' };
  }
}

const PreferenceMatchReport: React.FC = () => {
  const { activeGrid, loading: gridLoading } = useStabilizedGrid();
  const [intentions, setIntentions] = useState<TeachingIntention[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'fte-mismatch' | 'day-conflict' | 'unmet-top-area' | 'late'>('all');

  const year = useMemo(() => new Date().getFullYear() + 1, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await intentionsService.getIntentionsByYear(year);
      setIntentions(list);
    } catch (e: any) {
      setError(e?.message || 'Failed to load intentions');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  const result: PreferenceAnalyticsResult = useMemo(
    () => computePreferenceAnalytics(intentions, activeGrid),
    [intentions, activeGrid]
  );

  const filteredRows = useMemo(() => {
    switch (filter) {
      case 'fte-mismatch':    return result.rows.filter(r => r.fteMatch === 'over' || r.fteMatch === 'under');
      case 'day-conflict':    return result.rows.filter(r => r.dayMatch === 'conflict');
      case 'unmet-top-area':  return result.rows.filter(r => r.topAreaMet === 'not-met');
      case 'late':            return result.rows.filter(r => r.isLate);
      default:                return result.rows;
    }
  }, [result.rows, filter]);

  const handleExport = () => {
    const csv = analyticsRowsToCsv(filteredRows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `preference-match-${year}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const isLoading = gridLoading || loading;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        title="Preference Match Report"
        description={`Compares what teachers asked for in their intention form against what they've actually been allocated in the active grid, for the ${year} academic year.`}
        actions={
          <div className="flex gap-2">
            <button
              onClick={() => load()}
              disabled={isLoading}
              className="flex items-center gap-2 px-3 py-2 text-body-sm text-ink-700 bg-white border border-ink-300 rounded-md hover:bg-ink-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              disabled={filteredRows.length === 0}
              className="flex items-center gap-2 px-3 py-2 text-body-sm text-white bg-navy-600 border border-navy-600 rounded-md hover:bg-navy-700 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        }
      />

      {error && (
        <div className="mb-6 p-4 bg-danger-bg border border-danger-border rounded-lg text-danger-fg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="animate-pulse space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="h-28 bg-ink-200 rounded-lg" />
            <div className="h-28 bg-ink-200 rounded-lg" />
            <div className="h-28 bg-ink-200 rounded-lg" />
            <div className="h-28 bg-ink-200 rounded-lg" />
          </div>
          <div className="h-64 bg-ink-200 rounded-lg" />
        </div>
      ) : intentions.length === 0 ? (
        <div className="bg-white rounded-lg border border-ink-200 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-warn-fg mx-auto mb-4" />
          <h3 className="text-card-title text-ink-900 mb-2">
            No intentions for {year}
          </h3>
          <p className="text-ink-500 max-w-lg mx-auto">
            No teaching intentions have been submitted for the {year} academic year yet, so there is
            nothing to compare against the grid. Once teachers submit their intention forms, this
            report will show how their FTE, day-off, learning-area and RUAH preferences line up
            with their allocation.
          </p>
        </div>
      ) : (
        <>
          {/* No-allocation notice — makes the 0% metrics legible instead of alarming */}
          {result.summary.totalTeachersAllocated === 0 && (
            <div className="mb-6 p-4 bg-warn-bg border border-warn-border rounded-lg text-warn-fg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="text-body-sm">
                <span className="font-semibold">No allocations found in the active grid
                  {activeGrid?.name ? ` ("${activeGrid.name}")` : ''}.</span>{' '}
                {result.summary.totalTeachersWithIntentions} teacher{result.summary.totalTeachersWithIntentions === 1 ? '' : 's'} submitted
                intentions, but none of them are yet assigned to any class in this grid — so every
                match metric below reads as unmet. Match rates will populate once teachers are
                placed on lines in the grid.
              </div>
            </div>
          )}

          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <SummaryCard
              icon={<TrendingUp className="w-5 h-5 text-navy-600" />}
              title="FTE match"
              value={pct(result.summary.fteMatchRate)}
              sub={`${result.summary.fteOverAllocated} over · ${result.summary.fteUnderAllocated} under`}
            />
            <SummaryCard
              icon={<Calendar className="w-5 h-5 text-ok-fg" />}
              title="Day preference honoured"
              value={pct(result.summary.dayMatchRate)}
              sub={`${result.summary.dayConflictCount} conflicts`}
            />
            <SummaryCard
              icon={<Target className="w-5 h-5 text-navy-600" />}
              title="#1 learning area met"
              value={pct(result.summary.topAreaMatchRate)}
              sub={`Top-ranked area honoured`}
            />
            <SummaryCard
              icon={<Sparkles className="w-5 h-5 text-navy-600" />}
              title="RUAH satisfaction"
              value={pct(result.summary.ruahSatisfactionRate)}
              sub={`Got ≥ 1 of their RUAH picks`}
            />
          </div>

          {/* Counts row */}
          <div className="bg-white rounded-lg border border-ink-200 p-4 mb-6 flex flex-wrap gap-6 text-body-sm">
            <Stat label="Intentions submitted" value={result.summary.totalTeachersWithIntentions} />
            <Stat label="Allocated in current grid" value={result.summary.totalTeachersAllocated} />
            <Stat label="#1 subject met" value={pct(result.summary.topSubjectMatchRate)} />
            <Stat label="Late submissions" value={result.summary.lateSubmissionCount} tone={result.summary.lateSubmissionCount > 0 ? 'warn' : 'neutral'} />
            <Stat label="HR discuss flags" value={result.summary.discussFlagCount} tone={result.summary.discussFlagCount > 0 ? 'warn' : 'neutral'} />
          </div>

          {/* Filter chips */}
          <div className="bg-white rounded-lg border border-ink-200 p-4 mb-2 flex flex-wrap gap-2 items-center">
            <span className="text-body-sm font-medium text-ink-700 mr-2">Filter:</span>
            {([
              { id: 'all',             label: 'All teachers' },
              { id: 'fte-mismatch',    label: 'FTE mismatch' },
              { id: 'day-conflict',    label: 'Day conflict' },
              { id: 'unmet-top-area',  label: '#1 area unmet' },
              { id: 'late',            label: 'Late only' }
            ] as const).map(opt => (
              <button
                key={opt.id}
                onClick={() => setFilter(opt.id)}
                className={`px-3 py-1.5 text-body-sm rounded-full border transition-colors ${
                  filter === opt.id
                    ? 'bg-navy-600 text-white border-navy-600'
                    : 'bg-white text-ink-700 border-ink-300 hover:bg-ink-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
            <span className="ml-auto text-meta text-ink-500">
              Showing {filteredRows.length} of {result.rows.length} teachers
            </span>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-ink-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead className="bg-ink-50 border-b border-ink-200">
                  <tr className="text-left text-meta font-semibold text-ink-500 uppercase tracking-wider">
                    <th className="px-3 py-2">Teacher</th>
                    <th className="px-3 py-2">Learning Area</th>
                    <th className="px-3 py-2">FTE pref → alloc</th>
                    <th className="px-3 py-2">Days off → alloc days</th>
                    <th className="px-3 py-2">#1 area</th>
                    <th className="px-3 py-2">#1 subject</th>
                    <th className="px-3 py-2">RUAH</th>
                    <th className="px-3 py-2">Flags</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-body-sm text-ink-500">
                        No teachers match this filter.
                      </td>
                    </tr>
                  ) : filteredRows.map(row => {
                    const fte = fteBadge(row.fteMatch);
                    const day = dayBadge(row.dayMatch);
                    const area = metBadge(row.topAreaMet);
                    const subj = metBadge(row.topSubjectMet);
                    const ruah = ruahBadge(row.ruahSatisfaction);
                    return (
                      <tr key={row.staffId} className="hover:bg-ink-50">
                        <td className="px-3 py-2">
                          <div className="font-medium text-ink-900">{row.staffName}</div>
                          <div className="text-meta text-ink-500 font-mono tabular-nums">{row.staffId}</div>
                        </td>
                        <td className="px-3 py-2 text-ink-700">{row.department ? displayLearningArea(row.department) : '—'}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-meta text-ink-500 font-mono tabular-nums">{row.preferredFteLabel} → {row.allocatedFte.toFixed(2)}</span>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${fte.cls}`}>{fte.label}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-meta text-ink-500 truncate max-w-[180px]" title={`${row.dayOffPreference} / alloc: ${row.allocatedDays.join(', ')}`}>
                              {row.dayOffPreference} → {row.allocatedDays.length > 0 ? row.allocatedDays.join(', ') : '—'}
                            </span>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${day.cls}`}>{day.label}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-meta text-ink-500 truncate max-w-[140px]" title={row.topAreaRank1 || ''}>
                            {row.topAreaRank1 || <span className="text-ink-400 italic">none</span>}
                          </div>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${area.cls}`}>{area.label}</span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-meta text-ink-500 truncate max-w-[140px]" title={row.topSubjectRank1 || ''}>
                            {row.topSubjectRank1 || <span className="text-ink-400 italic">none</span>}
                          </div>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${subj.cls}`}>{subj.label}</span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-meta text-ink-500 font-mono tabular-nums">
                            {row.ruahAllocatedCount}/{row.ruahPreferenceCount}
                          </div>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${ruah.cls}`}>{ruah.label}</span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            {row.isLate && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-warn-bg text-warn-fg border border-warn-border">
                                Late
                              </span>
                            )}
                            {row.hrDiscussFlag && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-navy-100 text-navy-800 border border-navy-200">
                                Discuss
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const SummaryCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  value: string;
  sub?: string;
}> = ({ icon, title, value, sub }) => (
  <div className="bg-white rounded-lg border border-ink-200 p-4">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-meta font-medium text-ink-500 uppercase tracking-wide">{title}</div>
        <div className="text-2xl font-bold text-ink-900 mt-1 font-mono tabular-nums">{value}</div>
        {sub && <div className="text-meta text-ink-500 mt-1">{sub}</div>}
      </div>
      <div className="p-2 rounded-full bg-ink-50">{icon}</div>
    </div>
  </div>
);

const Stat: React.FC<{
  label: string;
  value: string | number;
  tone?: 'neutral' | 'warn';
}> = ({ label, value, tone = 'neutral' }) => (
  <div className="flex items-center gap-2">
    <span className={`w-2 h-2 rounded-full ${statusDot(tone === 'warn' ? 'warn' : 'neutral')}`} />
    <span className="text-ink-500">{label}:</span>
    <span className="font-semibold text-ink-900 font-mono tabular-nums">{value}</span>
  </div>
);

export default PreferenceMatchReport;
